import { useEffect } from 'react';
import { useCartContext } from '@/providers/CartProvider';
import { IS_PREVIEW } from '@/utils/env';
import {
  pushToElevarDataLayer,
  getUserProperties,
  transformCartItemsToElevar,
  formatPrice,
} from './utils';
import { ElevarViewCartEvent } from './types';

/**
 * Elevar view cart tracking hook.
 * Fires dl_view_cart event when user views cart or mini cart.
 * Use this hook on cart page or when cart modal opens.
 */
export const useElevarViewCart = () => {
  const { cartItems, getCartTotalPrice } = useCartContext();

  useEffect(() => {
    if (IS_PREVIEW) return;
    if (cartItems.length === 0) return;

    const userProperties = getUserProperties();
    const cartTotal = formatPrice(getCartTotalPrice());

    // Transform cart items to impressions format for view cart
    const impressions = transformCartItemsToElevar(cartItems);

    const event: ElevarViewCartEvent = {
      event: 'dl_view_cart',
      user_properties: userProperties,
      cart_total: cartTotal,
      ecommerce: {
        currencyCode: 'USD',
        actionField: {
          list: 'Shopping Cart',
        },
        impressions,
      },
    };

    pushToElevarDataLayer(event);
  }, [cartItems, getCartTotalPrice]);
};

/**
 * Manual trigger for view cart event
 * Use this when opening cart modal or navigating to cart page
 */
export const triggerElevarViewCart = () => {
  if (IS_PREVIEW) return;

  // This will be called manually, so we need to get cart data differently
  // You might need to pass cartItems and getCartTotalPrice as parameters
  // or access them through a global context
  console.log('Manual view cart trigger - implement based on your cart access pattern');
};

export default useElevarViewCart;
